import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

// Import tenant API functions
import {
  authAPI,
  userAPI,
  setAuthTokens,
  clearAuthTokens,
  isAuthenticated,
} from "../services/api";

// Admin API configuration
const ADMIN_API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || "https://admin-api.dev.dz-metrics.com/v1";

// Create axios instance for admin API
const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Admin API request interceptor
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("admin_access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Admin API response interceptor for token refresh
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("admin_refresh_token");
        if (refreshToken) {
          const response = await axios.post(`${ADMIN_API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          setAdminAuthTokens(access_token, refreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return adminApi(originalRequest);
        }
      } catch (refreshError) {
        clearAdminAuthTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Admin API functions
const adminAuthAPI = {
  login: (credentials) => adminApi.post("/auth/login", credentials),
  logout: () => adminApi.post("/auth/logout"),
};

const adminUserAPI = {
  getProfile: () => adminApi.get("/users/me"),
};

// Utility functions for admin tokens
export const setAdminAuthTokens = (accessToken, refreshToken) => {
  localStorage.setItem("admin_access_token", accessToken);
  localStorage.setItem("admin_refresh_token", refreshToken);
};

export const clearAdminAuthTokens = () => {
  localStorage.removeItem("admin_access_token");
  localStorage.removeItem("admin_refresh_token");
};

export const isAdminAuthenticated = () => {
  return !!localStorage.getItem("admin_access_token");
};

// Decode JWT token payload without verification (for reading claims)
// Note: This is safe because we're just reading claims, not verifying authenticity
const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
};

// Check if admin token is an OAuth-issued token (from customer backend)
// OAuth tokens have tier="platform" claim
const isOAuthAdminToken = (token) => {
  const payload = decodeJwtPayload(token);
  return payload && payload.tier === "platform";
};

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  tier: null, // 'tenant' or 'platform'
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  LOAD_USER_START: "LOAD_USER_START",
  LOAD_USER_SUCCESS: "LOAD_USER_SUCCESS",
  LOAD_USER_FAILURE: "LOAD_USER_FAILURE",
  CLEAR_ERROR: "CLEAR_ERROR",
  UPDATE_USER: "UPDATE_USER",
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        tier: action.payload.tier,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        tier: action.payload.tier,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        tier: null,
        isLoading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        tier: null,
        isLoading: false,
        error: null,
      };

    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
        isLoading: false,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Auto-detect tier on app load based on which token exists
  useEffect(() => {
    const loadUser = async () => {
      const hasTenantToken = isAuthenticated();
      const hasAdminToken = isAdminAuthenticated();

      // Priority: platform token over tenant token if both exist
      if (hasAdminToken) {
        dispatch({ type: AUTH_ACTIONS.LOAD_USER_START });
        try {
          const adminToken = localStorage.getItem("admin_access_token");

          // Check if this is an OAuth-issued admin token (from customer backend)
          // These tokens have tier="platform" in the claims and are signed by customer backend
          // We can't call admin API with these tokens (different JWT secret), so decode locally
          if (isOAuthAdminToken(adminToken)) {
            const payload = decodeJwtPayload(adminToken);

            // Check token expiration
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              throw new Error("Token expired");
            }

            // Use claims from JWT payload directly
            const user = {
              id: payload.sub,
              email: payload.email,
              name: payload.email, // OAuth doesn't include name in token
              tier: "platform",
              role: payload.role || "super_admin",
            };

            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: { user, tier: "platform" },
            });
          } else {
            // Traditional admin backend token - call admin API
            const response = await adminUserAPI.getProfile();
            const userData = response.data;

            // Check if user has admin privileges
            if (!userData.is_admin && !userData.is_superuser) {
              throw new Error("Insufficient privileges for platform access");
            }

            // Map admin user to unified user state
            const user = {
              id: userData.id,
              email: userData.email,
              name: userData.username || userData.email,
              tier: "platform",
              role: userData.is_superuser ? "super_admin" : "admin",
            };

            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: { user, tier: "platform" },
            });
          }
        } catch (error) {
          console.error("Failed to load platform user:", error);
          dispatch({
            type: AUTH_ACTIONS.LOAD_USER_FAILURE,
            payload: error.response?.data?.message || error.message || "Failed to load platform user",
          });
          clearAdminAuthTokens();
        }
      } else if (hasTenantToken) {
        dispatch({ type: AUTH_ACTIONS.LOAD_USER_START });
        try {
          const response = await userAPI.getProfile();
          const userData = response.data;

          // Map tenant user to unified user state
          const user = {
            id: userData.id || userData.userId,
            email: userData.email,
            name: userData.fullName || userData.name || userData.email,
            tier: "tenant",
            role: userData.role || "tenant_admin",
            tenantId: userData.tenantId,
            tenantName: userData.company || userData.tenantName,
            linkedProviders: userData.linkedProviders || [],
          };

          dispatch({
            type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
            payload: { user, tier: "tenant" },
          });
        } catch (error) {
          console.error("Failed to load tenant user:", error);
          dispatch({
            type: AUTH_ACTIONS.LOAD_USER_FAILURE,
            payload: error.response?.data?.message || "Failed to load tenant user",
          });
          clearAuthTokens();
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    loadUser();
  }, []);

  /**
   * @deprecated Password-based authentication is deprecated. Use verifyMagicLink() or loginWithGoogle() instead.
   * This function is kept for backward compatibility only.
   */
  const loginTenant = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await authAPI.login(credentials);
      const { access_token, refresh_token, user: loggedInUser } = response.data;

      setAuthTokens(access_token, refresh_token);

      const user = {
        id: loggedInUser.id,
        email: loggedInUser.email,
        name: loggedInUser.fullName || loggedInUser.email,
        tier: "tenant",
        role: "tenant_admin",
        tenantName: loggedInUser.company,
        linkedProviders: [],
      };

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, tier: "tenant" },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Tenant login failed";
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Login as platform user
  const loginPlatform = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await adminAuthAPI.login(credentials);
      const { access_token, refresh_token, user: userData } = response.data;

      // Check if user has admin privileges
      if (!userData.is_admin && !userData.is_superuser) {
        throw new Error("Insufficient privileges for platform access");
      }

      setAdminAuthTokens(access_token, refresh_token);

      const user = {
        id: userData.id,
        email: userData.email,
        name: userData.username || userData.email,
        tier: "platform",
        role: userData.is_superuser ? "super_admin" : "admin",
      };

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user, tier: "platform" },
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Platform login failed";
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Register tenant user using magic link (passwordless)
  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      // Request magic link for new user registration
      const response = await authAPI.requestMagicLink(userData.email);

      // Clear loading state but don't authenticate yet
      // User will authenticate via magic link in email
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      return {
        success: true,
        message: response.data?.message || "Magic link sent successfully"
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          "Registration failed";
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  };

  // Unified logout function
  const logout = async () => {
    try {
      if (state.tier === "platform") {
        await adminAuthAPI.logout();
        clearAdminAuthTokens();
      } else {
        await authAPI.logout();
        clearAuthTokens();
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear both token sets on logout to ensure clean state
      clearAuthTokens();
      clearAdminAuthTokens();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Update user function
  const updateUser = async (userData) => {
    try {
      const response = state.tier === "platform"
        ? await adminUserAPI.updateProfile(userData)
        : await userAPI.updateProfile(userData);

      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: response.data,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Update failed";
      return { success: false, error: errorMessage };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Magic link verification function
  const verifyMagicLink = useCallback(async (token) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await authAPI.verifyMagicLink(token);

      if (response.data.access_token) {
        setAuthTokens(response.data.access_token, response.data.refresh_token);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: response.data.user,
            tier: "tenant",
          },
        });
        return {
          success: true,
          redirectUrl: response.data.redirect_url || "/dashboard",
        };
      } else {
        throw new Error("No access token received");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message || "Magic link verification failed";
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  }, []);

  // Google OAuth login function (supports both tenant and platform users)
  const loginWithGoogle = useCallback(async (googleResponse) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      // First get the authorization URL from backend to get a state token
      const authResponse = await authAPI.googleAuthorize({ action: "login" });
      const { state } = authResponse.data;

      // Exchange the Google code with our backend
      const response = await authAPI.googleCallback(googleResponse.code, state);
      const { access_token, refresh_token, user: userData, isNewUser, is_admin, tier: responseTier, role: responseRole, redirectUrl } = response.data;

      // Check if this is a super admin OAuth login (platform tier)
      const isPlatformAdmin = is_admin || responseTier === "platform";

      if (isPlatformAdmin) {
        // Store as admin tokens for platform users
        setAdminAuthTokens(access_token, refresh_token);

        const user = {
          id: userData.id || userData.userId,
          email: userData.email,
          name: userData.fullName || userData.name || userData.email,
          tier: "platform",
          role: responseRole || userData.role || "super_admin",
        };

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, tier: "platform" },
        });

        return { success: true, isNewUser, redirectUrl: redirectUrl || "/admin", isPlatformAdmin: true };
      } else {
        // Standard tenant user login
        setAuthTokens(access_token, refresh_token);

        const user = {
          id: userData.id || userData.userId,
          email: userData.email,
          name: userData.fullName || userData.name || userData.email,
          tier: "tenant",
          role: userData.role || "tenant_admin",
          tenantId: userData.tenantId,
          tenantName: userData.company || userData.tenantName,
          linkedProviders: userData.linkedProviders || ["google"],
        };

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, tier: "tenant" },
        });

        return { success: true, isNewUser, redirectUrl };
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Google sign-in failed";

      // Handle account linking required
      if (error.response?.data?.error?.code === "EMAIL_EXISTS") {
        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: "An account with this email already exists. Please login with your password first to link your Google account.",
        });
        return {
          success: false,
          error: errorMessage,
          requiresLinking: true,
          email: error.response?.data?.error?.details?.email,
        };
      }

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });
      return { success: false, error: errorMessage };
    }
  }, []);

  // Register with Google (tenant-only, same flow as login)
  const registerWithGoogle = useCallback(async (googleResponse) => {
    return loginWithGoogle(googleResponse);
  }, [loginWithGoogle]);

  // Link Google account to existing tenant user
  const linkGoogleAccount = useCallback(async (googleResponse) => {
    try {
      // Get state for linking action
      const authResponse = await authAPI.googleAuthorize({ action: "link" });
      const { state } = authResponse.data;

      // Link the account
      const response = await authAPI.linkGoogle(googleResponse.code, state);

      // Update user state with new linked providers
      if (response.data.linkedProviders) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: { linkedProviders: response.data.linkedProviders },
        });
      }

      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Failed to link Google account";
      return { success: false, error: errorMessage };
    }
  }, []);

  // Unlink Google account (tenant-only)
  const unlinkGoogleAccount = useCallback(async () => {
    try {
      const response = await authAPI.unlinkGoogle();

      // Update user state
      if (response.data.linkedProviders) {
        dispatch({
          type: AUTH_ACTIONS.UPDATE_USER,
          payload: { linkedProviders: response.data.linkedProviders },
        });
      }

      return { success: true, message: response.data.message };
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Failed to unlink Google account";
      return { success: false, error: errorMessage };
    }
  }, []);

  // Helper methods computed from state
  const helpers = useMemo(() => {
    const user = state.user;
    const tier = state.tier;

    return {
      // Tier checks
      isTenant: tier === "tenant",
      isPlatform: tier === "platform",

      // Tenant role checks
      isTenantAdmin: tier === "tenant" && user?.role === "tenant_admin",
      isMember: tier === "tenant" && user?.role === "member",

      // Platform role checks
      isSuperAdmin: tier === "platform" && user?.role === "super_admin",
      isAdmin: tier === "platform" && user?.role === "admin",
      isSupport: tier === "platform" && user?.role === "support",

      // Permission checks
      canManageTenant:
        (tier === "tenant" && user?.role === "tenant_admin") ||
        (tier === "platform" && (user?.role === "super_admin" || user?.role === "admin")),
    };
  }, [state.user, state.tier]);

  /**
   * @deprecated Unified login function defaults to tenant login. Use verifyMagicLink() or loginWithGoogle() instead.
   */
  const login = loginTenant;

  const value = {
    ...state,
    ...helpers,
    login,
    loginTenant,
    loginPlatform,
    register,
    logout,
    updateUser,
    clearError,
    // OAuth methods (tenant-only)
    loginWithGoogle,
    registerWithGoogle,
    linkGoogleAccount,
    unlinkGoogleAccount,
    // Magic link authentication
    verifyMagicLink,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
